import { FC } from 'react';
import { Checkbox } from '@carbon/react';
import {
  ColumnDateFilter,
  FilterSection,
  useDataTableContext,
} from 'components/data-table';
import type { PackageLibraryResult } from './types';
import styles from './package-library-filters.module.scss';

// ─────────────────────────────────────
// Custom checkbox filters (no equivalent primitive in data-table yet)
// ─────────────────────────────────────

const ContainsInventoryCheckbox: FC = () => {
  const { table } = useDataTableContext<PackageLibraryResult>();
  const column = table.getColumn('containsInventoryAssets');
  const rawValue = column?.getFilterValue();
  const checked =
    Array.isArray(rawValue) && (rawValue as string[])[0] === 'true';

  return (
    <Checkbox
      id="pkg-lib-contains-inventory"
      labelText="Contains Inventory"
      checked={checked}
      onChange={(_e, { checked: next }) => {
        column?.setFilterValue(next ? ['true'] : undefined);
      }}
    />
  );
};

const InterestTypeCheckboxes: FC = () => {
  const { table } = useDataTableContext<PackageLibraryResult>();
  const column = table.getColumn('interestType');
  const selected = (column?.getFilterValue() as string[] | undefined) ?? [];

  const toggle = (value: string, next: boolean) => {
    const nextSet = new Set(selected);
    if (next) nextSet.add(value);
    else nextSet.delete(value);
    const nextArray = Array.from(nextSet);
    column?.setFilterValue(nextArray.length > 0 ? nextArray : undefined);
  };

  return (
    <>
      <span className={styles.groupLabel}>Package Interest Types</span>
      <Checkbox
        id="pkg-lib-interest-working"
        labelText="Working interest"
        checked={selected.includes('working')}
        onChange={(_e, { checked }) => toggle('working', checked)}
      />
      <Checkbox
        id="pkg-lib-interest-royalty"
        labelText="Royalty interest"
        checked={selected.includes('royalty')}
        onChange={(_e, { checked }) => toggle('royalty', checked)}
      />
    </>
  );
};

// ─────────────────────────────────────
// Filter panel
// ─────────────────────────────────────

const PackageLibraryFilters: FC = () => {
  return (
    <div className={styles.filtersWrapper}>
      <FilterSection title="Date Filters">
        <ColumnDateFilter columnId="createdAt" label="Created Date" />
        <ColumnDateFilter columnId="updatedAt" label="Updated Date" />
      </FilterSection>

      <FilterSection title="Package Details">
        <ContainsInventoryCheckbox />
        <InterestTypeCheckboxes />
      </FilterSection>
    </div>
  );
};

export { PackageLibraryFilters };
